import { z } from "zod";
export const GAME_PROTOCOL_VERSION = 1;
/**
 * Cosmetic cultivator skin ids. Optional everywhere and purely visual: the
 * server only passes it through (never validates semantics or uses it for
 * gameplay), and an absent / unknown value falls back to a hash-based skin on
 * the client. Keep in sync with the client SKIN_IDS registry.
 */
export const SkinIdSchema = z.enum([
    "qingyun",
    "moyu",
    "danxia",
    "xuanshuang",
    "zifu",
    "linglong",
]);
/**
 * Private-room configuration. Fully defined here for forward compatibility, but
 * ROOM-3 only ever sends the default config (no client config UI yet); map /
 * player-limit / bot combination validation lands in ROOM-4. Defaults let the
 * server treat "create with default config" as an empty object.
 */
export const RoomMapSizeSchema = z.enum(["small", "medium", "large"]);
export const MatchModeSchema = z.enum(["solo", "team"]);
export const WinConditionSchema = z.enum([
    "last_survivor",
    "timed_score",
    "team_timed_score",
    "team_elimination",
    "first_to_realm",
]);
export const PrivateRoomConfigSchema = z.object({
    mapSize: RoomMapSizeSchema.default("large"),
    playerLimit: z
        .union([
        z.literal(2),
        z.literal(4),
        z.literal(8),
        z.literal(12),
        z.literal(16),
        z.literal(30),
    ])
        .default(30),
    // Any integer up to the largest map cap; validatePrivateRoomConfig enforces
    // the real bound (bots must leave room for the host and one guest).
    botCount: z.number().int().min(0).max(30).default(0),
    matchMode: MatchModeSchema.default("solo"),
    teamCount: z.literal(2).optional(),
    winCondition: WinConditionSchema.default("last_survivor"),
    durationSeconds: z
        .union([z.literal(300), z.literal(600), z.literal(900)])
        .optional(),
    respawnEnabled: z.boolean().default(true),
    targetLevel: z.number().int().min(1).max(50).optional(),
});
/**
 * Room join intent carried by the handshake. Absent means the official quick
 * play flow, so old clients that never send it keep working unchanged.
 */
export const JoinIntentSchema = z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("official") }),
    z.object({
        kind: z.literal("private_create"),
        config: PrivateRoomConfigSchema.optional(),
    }),
    z.object({
        kind: z.literal("private_join"),
        inviteCode: z.string().trim().min(4).max(12),
    }),
]);
export const ClientHelloMessageSchema = z.object({
    type: z.literal("client_hello"),
    playerName: z.string().trim().min(1).max(24),
    playerKey: z.string().trim().min(8).max(80).optional(),
    anonymousPlayerId: z.string().trim().min(8).max(80).optional(),
    locale: z.string().trim().min(2).max(16).optional(),
    botToken: z.string().min(16).max(128).optional(),
    clientVersion: z.string().min(1),
    requestedLevel: z.number().int().min(1).max(50).optional(),
    // Cosmetic + forward-compatible: an unknown skin must not block joining.
    skinId: SkinIdSchema.optional().catch(undefined),
    // Absent => official quick play (keeps old clients working).
    joinIntent: JoinIntentSchema.optional(),
});
export const ClientInputMessageSchema = z.object({
    type: z.literal("client_input"),
    inputSeq: z.number().int().nonnegative(),
    angle: z.number().finite(),
    boost: z.boolean(),
    clientTime: z.number().int().nonnegative(),
});
export const ClientViewportMessageSchema = z.object({
    type: z.literal("client_viewport"),
    width: z.number().positive().finite(),
    height: z.number().positive().finite(),
    zoom: z.number().positive().finite().optional(),
    clientTime: z.number().int().nonnegative(),
});
export const ClientRespawnMessageSchema = z.object({
    type: z.literal("client_respawn"),
});
export const ClientHeartbeatMessageSchema = z.object({
    type: z.literal("client_heartbeat"),
    clientTime: z.number().int().nonnegative(),
});
/** Private-room owner starts the match (waiting -> playing). */
export const ClientRoomStartMessageSchema = z.object({
    type: z.literal("client_room_start"),
});
/** Eliminated player picks a new spectate target. */
export const ClientSpectateTargetMessageSchema = z.object({
    type: z.literal("client_spectate_target"),
    targetId: z.string().min(1),
});
/** Owner kicks a player from the private room. */
export const ClientRoomKickMessageSchema = z.object({
    type: z.literal("client_room_kick"),
    targetId: z.string().min(1),
});
/** Waiting-room team ids for 2-team modes. */
export const RoomTeamIdSchema = z.enum(["azure", "crimson"]);
/**
 * Set a member's waiting-room team. The server authorizes: the sender may set
 * their own team, or the owner may set anyone's. Only valid in team modes.
 */
export const ClientRoomSetTeamMessageSchema = z.object({
    type: z.literal("client_room_set_team"),
    targetPlayerId: z.string().min(1),
    teamId: RoomTeamIdSchema,
});
/** Owner rotates the invite code, invalidating old links. */
export const ClientRoomResetInviteMessageSchema = z.object({
    type: z.literal("client_room_reset_invite"),
});
/** Owner restarts the match from the settlement screen. */
export const ClientRoomRematchMessageSchema = z.object({
    type: z.literal("client_room_rematch"),
});
export const CHAT_MESSAGE_MAX_LENGTH = 140;
/** Private-room chat message from a client (server trims and rate-limits). */
export const ClientChatMessageSchema = z.object({
    type: z.literal("client_chat"),
    text: z.string().trim().min(1).max(CHAT_MESSAGE_MAX_LENGTH),
});
/** Private-room chat broadcast, stamped by the server. */
export const ServerChatMessageSchema = z.object({
    type: z.literal("server_chat"),
    protocolVersion: z.literal(GAME_PROTOCOL_VERSION),
    senderId: z.string().min(1),
    senderName: z.string().min(1),
    text: z.string().min(1).max(CHAT_MESSAGE_MAX_LENGTH),
    sentAt: z.number().int().nonnegative(),
});
/**
 * Room context attached to the welcome. Optional so old servers (and the plain
 * official path) can omit it; clients treat an absent room as official.
 */
export const WelcomeRoomSchema = z.object({
    kind: z.enum(["official", "private"]),
    inviteCode: z.string().min(1).optional(),
    isOwner: z.boolean(),
    state: z.enum(["waiting", "playing", "ended"]),
    ownerId: z.string().min(1).nullable(),
    config: PrivateRoomConfigSchema.optional(),
});
export const ServerWelcomeMessageSchema = z.object({
    type: z.literal("server_welcome"),
    protocolVersion: z.literal(GAME_PROTOCOL_VERSION),
    playerId: z.string().min(1),
    playerKey: z.string().min(8).max(80),
    roomId: z.string().min(1),
    tickRate: z.number().int().positive(),
    heartbeatIntervalMs: z.number().int().positive(),
    room: WelcomeRoomSchema.optional(),
});
/** A single occupant of a private-room waiting lobby. */
export const RoomMemberSchema = z.object({
    id: z.string().min(1),
    name: z.string().min(1),
    joinedAt: z.number().int().nonnegative(),
    isOwner: z.boolean(),
    isBot: z.boolean(),
    // Waiting-room team for 2-team modes; null/absent in solo modes.
    teamId: z.string().min(1).nullable().optional(),
    // Cosmetic: the member's chosen cultivator, for the lobby portrait. Unknown
    // skins from a newer peer degrade to undefined (hash fallback on the client).
    skinId: SkinIdSchema.optional().catch(undefined),
});
/**
 * Full waiting-lobby state, broadcast on join and whenever the roster, owner or
 * state changes. `ownerId` lets each client derive its own ownership.
 */
export const ServerRoomStateMessageSchema = z.object({
    type: z.literal("server_room_state"),
    protocolVersion: z.literal(GAME_PROTOCOL_VERSION),
    roomId: z.string().min(1),
    inviteCode: z.string().min(1).nullable(),
    state: z.enum(["waiting", "playing", "ended"]),
    ownerId: z.string().min(1).nullable(),
    config: PrivateRoomConfigSchema.optional(),
    players: z.array(RoomMemberSchema),
});
export const ServerRoomEventMessageSchema = z.object({
    type: z.literal("server_room_event"),
    protocolVersion: z.literal(GAME_PROTOCOL_VERSION),
    event: z.enum([
        "player_joined",
        "player_left",
        "owner_transferred",
        "room_started",
        "room_closed",
        "match_ended",
        "player_kicked",
        "invite_reset",
        "rematch",
    ]),
    payload: z.record(z.string(), z.unknown()),
});
export const TreasureKindSchema = z.enum([
    "qiankun_ruler",
    "hunyuan_fan",
    "spirit_gourd",
    "thunder_sword_box",
    "myriad_sword_token",
    "wind_thunder_boots",
    "black_tortoise_armor",
    "ascension_pearl",
]);
export const VisiblePlayerSchema = z.object({
    id: z.string().min(1),
    name: z.string().min(1),
    x: z.number().finite(),
    y: z.number().finite(),
    facing: z.number().finite(),
    radius: z.number().positive(),
    level: z.number().int().positive(),
    spiritStoneCount: z.number().int().nonnegative(),
    stamina: z.number().nonnegative(),
    maxStamina: z.number().positive(),
    turnBurstActive: z.boolean(),
    isAlive: z.boolean(),
    boostActive: z.boolean(),
    attackRadius: z.number().positive(),
    attackArcRadians: z.number().positive(),
    attackPhase: z.enum(["ready", "windup", "recovery"]),
    attackTargetId: z.string().min(1).nullable(),
    attackRemainingMs: z.number().nonnegative(),
    attackLastResult: z.enum(["hit", "miss"]).nullable(),
    treasureKind: TreasureKindSchema.nullable().optional(),
    treasureKinds: z.array(TreasureKindSchema).optional(),
    treasureSlotCount: z.number().int().min(1).max(3).optional(),
    spectatorCount: z.number().int().nonnegative().optional(),
    // Team id for team matches; absent/null in solo modes.
    teamId: z.string().min(1).nullable().optional(),
    // Cosmetic + forward-compatible: unknown skin from a newer peer -> undefined
    // (client renders a hash fallback), never throws on snapshot parse.
    skinId: SkinIdSchema.optional().catch(undefined),
});
export const VisibleStoneSchema = z.object({
    id: z.string().min(1),
    x: z.number().finite(),
    y: z.number().finite(),
    value: z.number().positive(),
});
export const UnclaimedTreasureSchema = z.object({
    id: z.string().min(1),
    kind: TreasureKindSchema,
    x: z.number().finite(),
    y: z.number().finite(),
    facing: z.number().finite(),
});
export const ResourceHeatmapBucketSchema = z.object({
    x: z.number().finite(),
    y: z.number().finite(),
    valueSum: z.number().nonnegative(),
    maxValue: z.number().nonnegative(),
    count: z.number().int().nonnegative(),
});
export const LeaderboardEntrySchema = z.object({
    rank: z.number().int().positive(),
    id: z.string().min(1),
    name: z.string().min(1),
    level: z.number().int().positive(),
    spiritStoneCount: z.number().int().nonnegative(),
    score: z.number().int().nonnegative().optional(),
    isAlive: z.boolean(),
    treasureKind: TreasureKindSchema.nullable().optional(),
    treasureKinds: z.array(TreasureKindSchema).optional(),
    spectatorCount: z.number().int().nonnegative().optional(),
    teamId: z.string().min(1).nullable().optional(),
});
export const PeriodLeaderboardEntrySchema = z.object({
    rank: z.number().int().positive(),
    name: z.string().min(1),
    playerKey: z.string().min(8).max(80).optional(),
    score: z.number().int().nonnegative(),
    isActive: z.boolean(),
});
export const PeriodLeaderboardsSchema = z.object({
    day: z.array(PeriodLeaderboardEntrySchema),
    week: z.array(PeriodLeaderboardEntrySchema),
    month: z.array(PeriodLeaderboardEntrySchema),
    updatedAt: z.number().int().nonnegative(),
});
export const LeaderboardMarkerSchema = z.object({
    rank: z.number().int().positive(),
    id: z.string().min(1),
    name: z.string().min(1),
    x: z.number().finite(),
    y: z.number().finite(),
    level: z.number().int().positive(),
    spiritStoneCount: z.number().int().nonnegative(),
});
/**
 * Private-room match context attached to snapshots: drives the in-match
 * countdown and the settlement transition. Absent on official rooms.
 */
export const TeamScoreSchema = z.object({
    teamId: z.string().min(1),
    score: z.number().int().nonnegative(),
});
export const MatchSnapshotSchema = z.object({
    endsAt: z.number().int().nonnegative().nullable(),
    phase: z.enum(["playing", "ended"]),
    // Live team totals for the team HUD; absent in solo modes.
    teams: z.array(TeamScoreSchema).optional(),
});
export const ServerSnapshotMessageSchema = z.object({
    type: z.literal("server_snapshot"),
    protocolVersion: z.literal(GAME_PROTOCOL_VERSION),
    serverTick: z.number().int().nonnegative(),
    self: VisiblePlayerSchema,
    visiblePlayers: z.array(VisiblePlayerSchema),
    visibleStones: z.array(VisibleStoneSchema),
    unclaimedTreasure: UnclaimedTreasureSchema.nullable().optional(),
    resourceHeatmap: z.array(ResourceHeatmapBucketSchema).optional(),
    leaderboard: z.array(LeaderboardEntrySchema).optional(),
    periodLeaderboards: PeriodLeaderboardsSchema.optional(),
    leaderboardMarkers: z.array(LeaderboardMarkerSchema).optional(),
    match: MatchSnapshotSchema.optional(),
    events: z.array(z.string()),
});
export const ServerEventMessageSchema = z.object({
    type: z.literal("server_event"),
    protocolVersion: z.literal(GAME_PROTOCOL_VERSION),
    event: z.enum([
        "death",
        "kill",
        "drop",
        "leaderboard",
        "respawn",
        "pickup",
        "attack_started",
        "attack_hit",
        "attack_missed",
        "treasure_guard",
        "treasure_spawned",
        "treasure_picked",
        "ascension",
        "score_settled",
        "ascension_started",
        "world_reborn",
    ]),
    payload: z.record(z.string(), z.unknown()),
});
export const ServerErrorMessageSchema = z.object({
    type: z.literal("server_error"),
    protocolVersion: z.literal(GAME_PROTOCOL_VERSION),
    code: z.enum([
        "invalid_message",
        "room_full",
        "room_not_found",
        "room_closed",
        "invalid_room_config",
    ]),
    message: z.string().min(1),
});
export const ClientMessageSchema = z.discriminatedUnion("type", [
    ClientHelloMessageSchema,
    ClientInputMessageSchema,
    ClientViewportMessageSchema,
    ClientRespawnMessageSchema,
    ClientHeartbeatMessageSchema,
    ClientRoomStartMessageSchema,
    ClientSpectateTargetMessageSchema,
    ClientRoomKickMessageSchema,
    ClientRoomResetInviteMessageSchema,
    ClientRoomRematchMessageSchema,
    ClientRoomSetTeamMessageSchema,
    ClientChatMessageSchema,
]);
export const ServerMessageSchema = z.discriminatedUnion("type", [
    ServerWelcomeMessageSchema,
    ServerSnapshotMessageSchema,
    ServerEventMessageSchema,
    ServerErrorMessageSchema,
    ServerRoomStateMessageSchema,
    ServerRoomEventMessageSchema,
    ServerChatMessageSchema,
]);
